// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek
//
// Controller-direct /DA sagas shared between the local LINX connection and the
// AWS cloud proxy connection. They are parameterized by a ComInterface (HTTP
// for LinxSagas, WebSocket for the proxy) and by the calling ApiSagas instance
// for the common error / period helpers.

import { put, select, delay } from "redux-saga/effects";

import {
  ActionType,
  raiseAction,
  IAction,
  IActionChangeInput,
  IActionChangeOutput,
  IActionConditionBlockStatus,
} from "actions";
import { IState } from "states/states";
import type { ComInterface } from "./cominterface";
import type { ApiSagas } from "./apisagas";

// raw LINX live data as read directly from the controller /DA endpoint
export interface IDataLinx {
  siteId?: number;
  systemTime: number;
  curTimeOfPeriod: string;
  accEnergy: string; // real energy, not power
  curPower: string; // Momentanleistung
  curTargetValues: string;
  curRegulationPriorityStatus: string;
  curTrendLevel: string;
  energyMeterValues: string;
  inputStatus: string;
  outputState: string;
  outputValue: string;
  outputValuePhysical: string;
  moduleStatus: string;
  conditionStatus: string;
  chargerCurP: string;
  chargerCurI: string;
  chargerCurU: string;
  chargerDevMaxI: string;
  chargerSetMaxI: string;
  chargerETotal: string;
  chargerESession: string;
  chargerState: string;
  chargerAuth: string;
}

const getState = (state: IState) => state;

// default LINX task time (ms); used to wait for the controller program to run
const LINX_TASK_RECURRENCE = 1000;

export function* getAllData(api: ApiSagas, com: ComInterface) {
  const opcItems = [
    "System Registers.System Time",
    "NV_CurTimeOfPeriod",
    "NV_AccEnergy",
    "NV_CurPower",
    "NV_TargetValues",
    "NV_RegulationPriorityStatus",
    "NV_CurTrendLevel",
    "NV_EnergyMeterValues",
    "NV_InputStatus",
    "NV_OutputState",
    "NV_OutputValue",
    "NV_OutputValuePhysical",
    "NV_ModuleStatus",
    "NV_ConditionStatus",
    "User Registers.ChargeCtrl.CurP",
    "User Registers.ChargeCtrl.CurI",
    "User Registers.ChargeCtrl.CurU",
    "User Registers.ChargeCtrl.DevMaxI",
    "User Registers.ChargeCtrl.SetMaxI",
    "User Registers.ChargeCtrl.ETotal",
    "User Registers.ChargeCtrl.ESession",
    "User Registers.ChargeCtrl.State",
    "User Registers.ChargeCtrl.Auth",
  ];
  try {
    const result = (yield com.read(opcItems)) as string[];
    if (Array.isArray(result) && result.length === opcItems.length) {
      let item = 0;
      const data: IDataLinx = {
        systemTime: parseInt(result[item++]),
        curTimeOfPeriod: result[item++],
        accEnergy: result[item++],
        curPower: result[item++],
        curTargetValues: result[item++],
        curRegulationPriorityStatus: result[item++],
        curTrendLevel: result[item++],
        energyMeterValues: result[item++],
        inputStatus: result[item++],
        outputState: result[item++],
        outputValue: result[item++],
        outputValuePhysical: result[item++],
        moduleStatus: result[item++],
        conditionStatus: result[item++],
        chargerCurP: result[item++],
        chargerCurI: result[item++],
        chargerCurU: result[item++],
        chargerDevMaxI: result[item++],
        chargerSetMaxI: result[item++],
        chargerETotal: result[item++],
        chargerESession: result[item++],
        chargerState: result[item++],
        chargerAuth: result[item++],
      };
      yield api.checkForNewPeriod(data.curTimeOfPeriod);
      yield put(raiseAction(ActionType.ACT_GET_ALLDATA_SUCC, data));
    } else {
      yield put(raiseAction(ActionType.ACT_GET_ALLDATA_FAIL, "received empty data"));
    }
  } catch (error) {
    yield api.raiseHttpError(ActionType.ACT_GET_ALLDATA_FAIL, error);
  }
}

// live status of the individual blocks of one condition, engine-computed and read on demand
// (not part of the regular polling cycle); the item name suffix addresses the condition by its
// index in parameter.condition, see engine ConditionHandler.comReadHandler
export function* getConditionBlockStatus(
  api: ApiSagas,
  com: ComInterface,
  action: IAction<number>,
) {
  const conditionIndex = action.payload;
  const opcItems = [
    `NV_CondBlockStatus_${conditionIndex}`,
    `NV_CondBlockValues_${conditionIndex}`,
    `NV_CondBlockConStatus_${conditionIndex}`,
  ];
  try {
    const result = (yield com.read(opcItems)) as string[];
    if (Array.isArray(result) && result.length === opcItems.length) {
      const data: IActionConditionBlockStatus = {
        conditionIndex,
        blockResult: result[0],
        dataValue: result[1],
        connectionStatus: result[2],
      };
      yield put(raiseAction(ActionType.ACT_GET_CONDITIONBLOCKSTATUS_SUCC, data));
    } else {
      yield put(raiseAction(ActionType.ACT_GET_CONDITIONBLOCKSTATUS_FAIL, "received empty data"));
    }
  } catch (error) {
    yield api.raiseHttpError(ActionType.ACT_GET_CONDITIONBLOCKSTATUS_FAIL, error);
  }
}

export function* setInput(api: ApiSagas, com: ComInterface, action: IAction<IActionChangeInput>) {
  const state = (yield select(getState)) as IState;
  const id = state.para.parameter?.input[action.payload.index]?.id;
  if (id === undefined) {
    yield put(
      raiseAction(ActionType.ACT_CHANGE_INPUT_FAIL, `Illegal input ${action.payload.index}.`),
    );
    return;
  }
  let result = false;
  try {
    // write value addressing the input by its id
    result = (yield com.writeItem({
      type: "input",
      id,
      value: action.payload.value,
    })) as boolean;
    if (result) {
      yield put(raiseAction(ActionType.ACT_CHANGE_INPUT_SUCC, action.payload));
    } else {
      yield put(raiseAction(ActionType.ACT_CHANGE_INPUT_FAIL, `Changing input failed.`));
    }
  } catch (error) {
    yield api.raiseHttpError(ActionType.ACT_CHANGE_INPUT_FAIL, error);
  }
}

export function* setOutput(
  api: ApiSagas,
  com: ComInterface,
  action: IAction<IActionChangeOutput>,
  taskRecurrence = LINX_TASK_RECURRENCE,
) {
  const state = (yield select(getState)) as IState;
  const id = state.para.parameter?.output[action.payload.index]?.id;
  if (id === undefined) {
    yield put(
      raiseAction(ActionType.ACT_CHANGE_OUTPUT_FAIL, `Illegal output ${action.payload.index}.`),
    );
    return;
  }
  let result = false;
  try {
    // write command addressing the output by its id
    result = (yield com.writeItem({
      type: "output",
      id,
      cmd: action.payload.cmd,
    })) as boolean;
    if (result) {
      yield put(raiseAction(ActionType.ACT_CHANGE_OUTPUT_SUCC, action.payload));
      // load new state
      // we need to delay to let the logi.cad program run one task
      yield delay(2 * taskRecurrence);
      yield put(raiseAction(ActionType.ACT_GET_ALLDATA_REQ));
    } else {
      yield put(raiseAction(ActionType.ACT_CHANGE_OUTPUT_FAIL, `Changing output failed.`));
    }
  } catch (error) {
    yield api.raiseHttpError(ActionType.ACT_CHANGE_OUTPUT_FAIL, error);
  }
}
